import React from 'react';
import {
  Box,
  Flex,
  IconButton,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {Add, Close, TrashCan} from '@carbon/icons-react';
import Select from 'react-select';
import {
  IntentSignalDisplayField,
  IntentGroup,
  IntentGroupLabels,
  useIntentModelStore,
} from './IntentModeStore';
import _ from 'lodash';
import {
  IntentGroupOption,
  useIntentGroupOptions,
} from './useIntentGroupOptions';

export const IntentSignalGroup = ({
  group,
  fields,
}: {
  group: IntentGroup;
  fields: IntentSignalDisplayField[];
}) => {
  const {addPlaceholderField: addField, removeGroup} = useIntentModelStore();
  const options = useIntentGroupOptions()[group];

  const canAddField =
    fields.every(({type}) => type === 'field') &&
    options.some(({isDisabled}) => !isDisabled);

  return (
    <Flex
      bgColor="kgray.100"
      border="1px"
      borderColor="kgray.200"
      borderRadius="md"
      direction="column"
      gap="4"
      px="4"
      py="2"
    >
      <Flex alignItems="center" justifyContent="space-between" px="1">
        <Text>{IntentGroupLabels[group]}</Text>
        <Flex gap={2}>
          <IconButton
            aria-label="Add signal"
            icon={<Add />}
            isDisabled={!canAddField}
            onClick={() => addField(group)}
            size="sm"
            variant="ghost"
          />
          <IconButton
            aria-label="Delete all signals"
            icon={<TrashCan />}
            onClick={() => removeGroup(group)}
            size="sm"
            variant="ghost"
          />
        </Flex>
      </Flex>
      <Table
        sx={{
          'td, th': {
            padding: '1',
          },
        }}
        variant="unstyled"
      >
        <colgroup>
          <col width="50%" />
          <col width="50%" />
          <col width="auto" />
        </colgroup>
        <Thead>
          <Tr>
            <Th>Signal</Th>
            <Th>Weight</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {fields.map((field) => (
            <IntentSignalRow
              key={field.id.toString()}
              field={field}
              options={options}
            />
          ))}
        </Tbody>
      </Table>
    </Flex>
  );
};

const IntentSignalRow = ({
  field,
  options,
}: {
  field: IntentSignalDisplayField;
  options: IntentGroupOption[];
}) => {
  const {updateField, removeField} = useIntentModelStore();

  return (
    <Tr key={field.id.toString()}>
      <Td>
        <Box>
          <Select
            options={options}
            onChange={(option) => {
              if (!option) {
                return;
              }

              updateField(field.id, {
                type: 'field',
                id: option.value,
                weight: field.type === 'field' ? field.weight : undefined,
                group: field.group,
              });
            }}
            value={options.find((option) => _.isEqual(option.value, field.id))}
          />
        </Box>
      </Td>
      <Td>
        <NumberInput
          isDisabled={field.type !== 'field'}
          isRequired
          min={0}
          onChange={(_, valueAsNumber) => {
            if (field.type !== 'field') {
              return;
            }

            updateField(field.id, {
              type: 'field',
              id: field.id,
              weight: !isNaN(valueAsNumber) ? valueAsNumber : undefined,
              group: field.group,
            });
          }}
          value={field.type === 'field' ? (field.weight ?? '') : ''}
        >
          <NumberInputField bgColor="kgray.50" />
        </NumberInput>
      </Td>
      <Td>
        <IconButton
          aria-label="Remove signal"
          icon={<Close />}
          size="sm"
          variant="ghost"
          onClick={() => removeField(field.id)}
        />
      </Td>
    </Tr>
  );
};
